/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CoverageBucket,
  ListFleetDevicesRequest,
  ListFleetDevicesSecurity,
  PolicyState,
} from "../models/operations/listfleetdevices.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildFleetDevicesInfiniteQuery,
  buildFleetDevicesQuery,
  FleetDevicesInfiniteQueryData,
  FleetDevicesPageParams,
  FleetDevicesQueryData,
  prefetchFleetDevices,
  prefetchFleetDevicesInfinite,
  queryKeyFleetDevices,
  queryKeyFleetDevicesInfinite,
} from "./fleetDevices.core.js";
export {
  buildFleetDevicesInfiniteQuery,
  buildFleetDevicesQuery,
  type FleetDevicesInfiniteQueryData,
  type FleetDevicesPageParams,
  type FleetDevicesQueryData,
  prefetchFleetDevices,
  prefetchFleetDevicesInfinite,
  queryKeyFleetDevices,
  queryKeyFleetDevicesInfinite,
};

export type FleetDevicesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listFleetDevices deviceIntegrations
 *
 * @remarks
 * Page through every device the org can see, newest first: its connected MDM inventories plus the machines running the agent that no inventory holds, each with its coverage bucket and policy state.
 */
export function useFleetDevices(
  request?: ListFleetDevicesRequest | undefined,
  security?: ListFleetDevicesSecurity | undefined,
  options?: QueryHookOptions<FleetDevicesQueryData, FleetDevicesQueryError>,
): UseQueryResult<FleetDevicesQueryData, FleetDevicesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildFleetDevicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listFleetDevices deviceIntegrations
 *
 * @remarks
 * Page through every device the org can see, newest first: its connected MDM inventories plus the machines running the agent that no inventory holds, each with its coverage bucket and policy state.
 */
export function useFleetDevicesSuspense(
  request?: ListFleetDevicesRequest | undefined,
  security?: ListFleetDevicesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    FleetDevicesQueryData,
    FleetDevicesQueryError
  >,
): UseSuspenseQueryResult<FleetDevicesQueryData, FleetDevicesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildFleetDevicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listFleetDevices deviceIntegrations
 *
 * @remarks
 * Page through every device the org can see, newest first: its connected MDM inventories plus the machines running the agent that no inventory holds, each with its coverage bucket and policy state.
 */
export function useFleetDevicesInfinite(
  request?: ListFleetDevicesRequest | undefined,
  security?: ListFleetDevicesSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    FleetDevicesInfiniteQueryData,
    FleetDevicesQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<FleetDevicesInfiniteQueryData, FleetDevicesPageParams>,
  FleetDevicesQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    FleetDevicesInfiniteQueryData,
    FleetDevicesQueryError,
    InfiniteData<FleetDevicesInfiniteQueryData, FleetDevicesPageParams>,
    QueryKey,
    FleetDevicesPageParams
  >({
    ...buildFleetDevicesInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listFleetDevices deviceIntegrations
 *
 * @remarks
 * Page through every device the org can see, newest first: its connected MDM inventories plus the machines running the agent that no inventory holds, each with its coverage bucket and policy state.
 */
export function useFleetDevicesInfiniteSuspense(
  request?: ListFleetDevicesRequest | undefined,
  security?: ListFleetDevicesSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    FleetDevicesInfiniteQueryData,
    FleetDevicesQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<FleetDevicesInfiniteQueryData, FleetDevicesPageParams>,
  FleetDevicesQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    FleetDevicesInfiniteQueryData,
    FleetDevicesQueryError,
    InfiniteData<FleetDevicesInfiniteQueryData, FleetDevicesPageParams>,
    QueryKey,
    FleetDevicesPageParams
  >({
    ...buildFleetDevicesInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setFleetDevicesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider?: string | undefined;
      inInventory?: boolean | undefined;
      coverageBucket?: CoverageBucket | undefined;
      policyState?: PolicyState | undefined;
      userIds?: Array<string> | undefined;
      userEmails?: Array<string> | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: FleetDevicesQueryData,
): FleetDevicesQueryData | undefined {
  const key = queryKeyFleetDevices(...queryKeyBase);

  return client.setQueryData<FleetDevicesQueryData>(key, data);
}

export function invalidateFleetDevices(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider?: string | undefined;
      inInventory?: boolean | undefined;
      coverageBucket?: CoverageBucket | undefined;
      policyState?: PolicyState | undefined;
      userIds?: Array<string> | undefined;
      userEmails?: Array<string> | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "deviceIntegrations",
      "listFleetDevices",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllFleetDevices(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deviceIntegrations", "listFleetDevices"],
  });
}
